import { useState } from 'react';
import { useLocation } from 'wouter';
import { useStats, useDataQuality, useRulesData } from '@/hooks/useLocalStorage';
import { 
  FileSliders, 
  Layers, 
  GitCommit,
  UploadCloud ,
  Table, 
  Search, 
  Shield, 
  ArrowUpDown, 
  History, 
  Bot, 
  Settings,
  ExternalLink,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

const navigation = [
  { name: 'Rules Manager', href: '/', icon: Table },
  { name: 'Rules Function', href: '/rulefunction', icon: Layers },
  { name: 'Rules Version', href: '/ruleversion', icon: GitCommit },
  { name: 'Rules Publish', href: '/rulepublish', icon: UploadCloud },
  // { name: 'Search & Resolve', href: '/search', icon: Search },
  { name: 'Data Quality', href: '/quality', icon: Shield },
  { name: 'Import / Export', href: '/import', icon: ArrowUpDown },
  { name: 'Audit Log', href: '/audit', icon: History },
  { name: 'AI Rule Console', href: '/ai-builder', icon: Bot },
  // { name: 'AI / NLPS Console', href: '/ai', icon: Bot },
  // { name: 'Settings', href: '/settings', icon: Settings },
];

export function Sidebar({ open, onToggle, isMobile }: SidebarProps) {
  const [location, setLocation] = useLocation();
  const { data: stats } = useStats();
  const { data: dqIssues } = useDataQuality();
  const { data: types } = useRulesData(1, 10);
  const dqIssueCount = dqIssues?.length || 0;

  const handleNavClick = (href: string) => {
    setLocation(href);
    // Close sidebar on mobile after navigation
    if (isMobile) {
      onToggle();
    }
  };

  return (
    <aside className={cn(
      "bg-white border-r border-gray-200 shadow-sm transition-all duration-300",
      isMobile 
        ? "fixed left-0 top-0 h-full z-50 w-64" + (open ? " translate-x-0" : " -translate-x-full")
        : ((open ? "w-64" : "w-16") + " sticky top-0 h-full"),
      "flex flex-col"
    )}>
      {/* Mobile header */}
      {isMobile && open && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
          <Button variant="ghost" size="sm" onClick={onToggle} className="p-1">
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      <div className={cn("p-4", isMobile && open ? "pt-0" : "") }>
        <nav className="space-y-2">
          {navigation.map((item) => {
            const isActive = location === item.href;
            return (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  "w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors relative",
                  isActive 
                    ? "bg-workfinity-50 text-workfinity-700 border border-workfinity-200" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                title={!open && !isMobile ? item.name : undefined}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {(open || isMobile) && (
                  <>
                    <span className="font-medium text-sm md:text-base">{item.name}</span>
                    {item.name === 'Data Quality' && dqIssueCount > 0 && (
                      <span className="ml-auto bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                        {dqIssueCount}
                      </span>
                    )}
                  </>
                )}
                {!open && !isMobile && isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-workfinity-600 rounded-r-full -ml-4" />
                )}
              </button>
            );
          })}
        </nav>
      </div>
      
      {(open || isMobile) && (
        <div className="border-t border-gray-200 p-4 bg-white">
          <div className="text-md text-gray-500 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col">
                <span>Rules</span>
                <span className="font-mono font-semibold text-gray-700">{types?.total || 0}</span>
              </div>
              {/* <div className="flex flex-col">
                <span>Codes</span>
                <span className="font-mono text-lg font-semibold text-gray-700">{allCodes?.length || 0}</span>
              </div> */}
            </div>
            <div className="pt-2 border-t border-gray-200">
              <a 
                href="https://ipcs-bprm.workfinity.com/login.aspx?encrypt=1&key=NjYwZjRhMzYtNzJjZi00YzViLWIxOTAtOTBlNmRmZmU2Mzg0" 
                className="flex items-center space-x-1 text-workfinity-600 hover:text-workfinity-700 text-xs underline" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Legacy System</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
